import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Search, Loader2, Package, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { trackOrder } from '@/lib/api/endpoints';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rupees);
};

const STATUS_LABELS = {
  pending: 'Pending',
  processing: 'Composing',
  'on-hold': 'On hold',
  completed: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  failed: 'Failed',
};

const TrackOrder = () => {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');

  const lookup = useMutation({
    mutationFn: ({ order_id, email }) => trackOrder(order_id, email),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) return;
    lookup.mutate({ order_id: orderId.trim(), email: email.trim() });
  };

  return (
    <main className="container-lux py-16 md:py-24 max-w-2xl">
      <div className="text-center mb-10">
        <p className="tracking-luxe text-[#b78d3c] mb-3">Track</p>
        <h1 className="font-display text-5xl md:text-6xl">Find your order</h1>
        <p className="text-[#1a120b]/70 mt-4 max-w-md mx-auto">
          Enter the order ID and the email used at checkout. We'll surface the
          status and what's next.
        </p>
      </div>

      <Card className="border-[#1a120b]/10 bg-[#f6efe1]/40">
        <CardHeader>
          <CardTitle className="font-display text-2xl text-[#1a120b]">
            Order details
          </CardTitle>
          <CardDescription className="text-[#1a120b]/70">
            Both fields are required.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="orderId" className="text-[#1a120b]">
                Order ID
              </Label>
              <Input
                id="orderId"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 1042"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="bg-[#f6efe1] border-[#1a120b]/20"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1a120b]">
                Email used at checkout
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#f6efe1] border-[#1a120b]/20"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={lookup.isPending}
              className="w-full bg-[#1a120b] hover:bg-[#1a120b]/90 text-[#f6efe1]"
            >
              {lookup.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {lookup.isPending ? 'Looking up…' : 'Find order'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {lookup.error && (
        <div className="mt-6 p-5 border border-red-200 bg-red-50 text-red-700 text-sm">
          {lookup.error.message}
        </div>
      )}

      {lookup.data && (
        <Card className="mt-6 border-[#1a120b]/10 bg-[#efe6d3]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="font-display text-2xl text-[#1a120b] flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#b78d3c]" />
                  Order #{lookup.data.id}
                </CardTitle>
                <CardDescription className="text-[#1a120b]/70">
                  Placed{' '}
                  {lookup.data.date_created
                    ? new Date(lookup.data.date_created).toLocaleString('en-IN', {
                        dateStyle: 'long',
                        timeStyle: 'short',
                      })
                    : '—'}
                </CardDescription>
              </div>
              <span className="text-xs tracking-widest uppercase text-[#b78d3c] font-medium">
                {STATUS_LABELS[lookup.data.status] || lookup.data.status}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {lookup.data.line_items?.length > 0 && (
              <ul className="divide-y divide-[#1a120b]/12">
                {lookup.data.line_items.map((item, i) => (
                  <li key={i} className="py-2 flex justify-between text-sm">
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span>
                      {formatPrice(String(item.total), lookup.data.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-between font-display text-xl pt-3 border-t border-[#1a120b]/15">
              <span>Total</span>
              <span>{formatPrice(String(lookup.data.total), lookup.data.currency)}</span>
            </div>
            <Link
              to="/shop"
              className="inline-flex items-center gap-1 text-sm text-[#b78d3c] hover:underline"
            >
              Continue shopping <ChevronRight className="w-3 h-3" />
            </Link>
          </CardContent>
        </Card>
      )}
    </main>
  );
};

export default TrackOrder;
